import { isApiError } from "@bw/shared/api";

function capacityCount(
  details: Record<string, unknown> | undefined,
  key: "maximum" | "accepted" | "pending",
): number | null {
  const value = details?.[key];
  return typeof value === "number" &&
    Number.isInteger(value) &&
    value >= (key === "maximum" ? 1 : 0) &&
    value <= 6
    ? value
    : null;
}

export function partyFullMessage(
  details: Record<string, unknown> | undefined,
): string {
  const maximum = capacityCount(details, "maximum");
  const accepted = capacityCount(details, "accepted");
  const pending = capacityCount(details, "pending");
  if (
    maximum === null ||
    accepted === null ||
    pending === null ||
    accepted + pending < maximum
  ) {
    return "This party is full.";
  }
  return `Party is full: max ${maximum}; ${accepted} accepted, ${pending} pending invitations`;
}

/**
 * Map a party-invite failure to honest inline copy.
 *
 * Used by the facility-scoped `InvitePicker`, the sole invite surface on
 * `PartyDetailPage`. Keeping this mapping outside the component makes each
 * stable backend outcome explicit and independently testable.
 *
 * The friend-gate is gone (slice 2): a generic 403 maps to honest "can't invite"
 * copy, never the stale "only friends" language.
 */
export function mapInviteError(err: unknown): string {
  if (isApiError(err)) {
    if (err.code === "DUPLICATE_INVITE") {
      return "They're already invited to this party.";
    }
    if (err.code === "NO_ACTIVE_DROP_IN") {
      return "This player is no longer checked in at this facility.";
    }
    if (err.code === "PARTY_FULL") {
      return partyFullMessage(err.details);
    }
    if (err.code === "PARTY_NOT_OPEN") {
      return "This party is no longer accepting players.";
    }
    if (err.status === 403) {
      return "You can't invite this player right now.";
    }
  }
  return "We couldn't send that invite. Please try again.";
}
